/**
 * @file: Notifications.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { config } from '@euler/config';
import { PermissionStatus } from 'expo-modules-core';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { CloudPush } from '../../lib/notification/CloudPush';

export const notificationState$ = new BehaviorSubject<
  | { status: 'pending' }
  | { status: 'rejected' }
  | { status: 'granted'; deviceId: string; apnsDeviceToken?: string }
>({
  status: 'pending',
});

export async function requestNotificationUserPermissions() {
  if (!config.cloudPush.enabled) return;

  try {
    let authorizationStatus = await CloudPush.getPermissionsAsync();
    // On iOS ask on every launch, granted or not: once decided it shows no
    // prompt, and it is what registers for remote notifications, the only
    // way Aliyun learns the current APNs token. Asked only until granted,
    // a token that changed (a restored or new phone) was never sent, and
    // pushes went to the old one and vanished until a reinstall.
    if (
      authorizationStatus.status !== PermissionStatus.GRANTED ||
      Platform.OS === 'ios'
    ) {
      authorizationStatus = await CloudPush.requestPermissionsAsync();
    }
    if (
      authorizationStatus.status === PermissionStatus.GRANTED ||
      Platform.OS === 'android'
    ) {
      await CloudPush.register();
      const deviceId = await CloudPush.getDeviceId();
      const apnsDeviceToken =
        Platform.OS === 'ios'
          ? await CloudPush.getApnsDeviceToken()
          : undefined;
      if (Platform.OS === 'ios') {
        __DEV__ &&
          console.log(
            `[cloudpush][${Platform.OS}] successfully registered with device id: `,
            deviceId,
            ', apns device token: ',
            apnsDeviceToken,
          );
      } else {
        __DEV__ &&
          console.log(
            `[cloudpush][${Platform.OS}] successfully registered with device id: `,
            deviceId,
          );
      }
      notificationState$.next({
        status: 'granted',
        deviceId,
        apnsDeviceToken,
      });
    } else {
      notificationState$.next({
        status: 'rejected',
      });
    }
  } catch (e) {
    console.error(e);
    notificationState$.next({
      status: 'rejected',
    });
  }
}
